import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { DOSSIER, naamUitKop } from "../lib/dossier";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import ChatArea from "../components/ChatArea";
import SettingsDialog from "../components/SettingsDialog";
import EersteStart from "../components/EersteStart";
import { LicentieMelding } from "../components/Licentie";
import { tabbladUit } from "../lib/instellingenadres";
import SearchDialog from "../components/SearchDialog";
import TimeMachine from "../components/TimeMachine";
import UpgradeDialog from "../components/UpgradeDialog";
import ClawConfirmDialog from "../components/ClawConfirmDialog";
import EscalationConfirmDialog from "../components/EscalationConfirmDialog";
import { useAuth } from "../contexts/AuthContext";
import { PROVIDERS, findModelLabel } from "../lib/providers";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { houdBij } from "@/lib/wachtendeVoorstellen";
import { maakLezer } from "@/lib/sse";
import { beginBeurt, behoudDenktijd, pasFaseToe, haalBeurtWeg, vervangAntwoord } from "@/lib/beurt";
import WachtInGeheugen from "../components/WachtInGeheugen";
import { runCouncil } from "../lib/council-stream";
import { initialSidebarOpen } from "../lib/viewport";
import useIsNarrow from "../hooks/use-narrow";
import { labelVoorBank, labelVoorProject, soortVanRegels } from "../lib/switch-labels";
import { alleBanken, koppelProject, zetOm } from "../lib/switch-toggle";
import { useTaal } from "@/contexts/TaalContext";
import { useGesprekConnectors } from "@/lib/gesprekConnectors";
import { useGesprekModel } from "@/lib/gesprekModel";
import { PERSOONLIJK, WISSEL_BERICHT, leesActiefTeam, verplaatsGesprek, volgWissel } from "@/lib/actiefTeam";
import { exportBlob } from "@/lib/exportBlob";


export default function ChatPage() {
  const { t } = useTaal();
  const { conversationId } = useParams();
  // I7: de connectors van het plusmenu, voor dit gesprek of voor het nieuwe.
  const gesprekConnectors = useGesprekConnectors(conversationId);
  // MK2 (3 nov): de modelkeuze per gesprek; op het welkomstscherm wacht zij tot het gesprek er is.
  const gesprekModel = useGesprekModel(conversationId);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  // De telling van het voorstel-bakje, BLOK 7 fase A.
  const [suggestedCount, setSuggestedCount] = useState(0);
  const [postbusCount, setPostbusCount] = useState(0);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  // Open on a desktop, out of the way on a phone. See lib/viewport.js.
  const [isSidebarOpen, setIsSidebarOpen] = useState(initialSidebarOpen);
  const isNarrow = useIsNarrow();
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Het tabblad waarop Instellingen opent. "Koppelingen beheren" in het plusmenu
  // opent sinds 23 oktober het Koppelingen-venster en niet meer Instellingen.
  const [settingsTab, setSettingsTab] = useState("provider");
  // G4 (Frank, 18 okt 2026): een oud adres als /store of /network komt hier aan
  // als `?instellingen=<tabblad>` (zie `lib/instellingenadres.js`) en opent
  // Instellingen op dat tabblad. De parameter gaat daarna uit het adres; de
  // melding van Stripe (`success`, `cancelled`) blijft staan voor de winkel.
  const [zoekParams, setZoekParams] = useSearchParams();
  useEffect(() => {
    const tabblad = tabbladUit(zoekParams);
    if (!tabblad) return;
    setSettingsTab(tabblad);
    setSettingsOpen(true);
    const rest = new URLSearchParams(zoekParams);
    rest.delete("instellingen");
    setZoekParams(rest, { replace: true });
  }, [zoekParams, setZoekParams]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [timeMachineOpen, setTimeMachineOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  // Shown in the header while it is on, so going online is never something
  // that happens without the screen saying so.
  const [webAccess, setWebAccess] = useState(false);
  const [vaultWrite, setVaultWrite] = useState(false);
  // FIX 1: het gesprek waarin nu een beurt loopt. `fetchMessages` laat dat
  // gesprek met rust tot de beurt klaar is.
  const lopendeBeurt = useRef(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [orchestrateMode, setOrchestrateMode] = useState(false);
  const [councilMode, setCouncilMode] = useState(null); // null = off, else "synthesis" | "consensus" | "best_of_n"
  // Starts as null, meaning "not known yet". Finding out whether there are
  // two models to convene involves probing Ollama, so the answer arrives
  // after the page does. Null leaves the button alone in the meantime rather
  // than disabling it for a second on every load.
  const [councilStatus, setCouncilStatus] = useState(null);
  const [escalationPlan, setEscalationPlan] = useState(null);   // routing preflight awaiting confirmation
  // Something Novamund noticed about you, offered with one answer. Not stored
  // anywhere until it is accepted, and gone the moment you leave.
  const [proposal, setProposal] = useState(null);
  // What happened to something he asked to be remembered. Held next to the
  // proposal and cleared with it, for the same reason: the refetch after an
  // answer would wipe anything hung on the message itself.
  const [notice, setNotice] = useState(null);
  const [pendingSend, setPendingSend] = useState(null);         // the message that triggered it
  const [escalationDecided, setEscalationDecided] = useState(false); // "do not ask again", this conversation
  const [trailByMessage, setTrailByMessage] = useState({});     // message_id -> flight recorder entry
  const [currentProvider, setCurrentProvider] = useState("anthropic");
  const [currentModelId, setCurrentModelId] = useState("claude-sonnet-5");
  const [currentModel, setCurrentModel] = useState("Claude Sonnet 5");
  const [pendingClawAction, setPendingClawAction] = useState(null);
  const [clawConfirmOpen, setClawConfirmOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  // De banken die standaard UIT staan in het project van dit gesprek, en welke
  // ervan hier zijn aangezet (R17-b punt 4).
  //
  // TWEE BRONNEN MET EEN DUIDELIJKE TAAKVERDELING, en dat is met opzet. WELKE
  // banken uit staan komt van het project, want die vraag heeft ook antwoord in
  // een vers gesprek dat nog geen id heeft. WELKE ervan aan staan komt van het
  // gesprek, en alleen na een herlading; tijdens het typen is de lokale
  // toestand de waarheid en zij reist mee met het bericht.
  const [archiveBases, setArchiveBases] = useState([]);
  const [archiveOn, setArchiveOn] = useState([]);

  const handleNewChat = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/conversations${selectedProjectId ? `?project_id=${selectedProjectId}` : ''}`);
      const newConv = response.data;
      setConversations(prev => [newConv, ...prev]);
      navigate(`/chat/${newConv.id}`);
    } catch (error) {
      toast.error(t("Nieuwe chat aanmaken mislukt"));
    }
  }, [selectedProjectId, navigate, t]);

  // Welke banken standaard uit staan. Op het PROJECT en niet op het gesprek,
  // want een vers gesprek bestaat nog niet en juist daar wil je de schakelaar
  // al zien.
  //
  // ZONDER PROJECT WORDEN ZE ALLEMAAL OPGEHAALD (R26-d, 22 augustus 2026). Hier
  // stond een vroege terugkeer met een lege lijst, en dat was precies het
  // gebrek: in een gewoon gesprek bestond de schakelaar niet, dus waren de
  // Archief-banken onbereikbaar en werkte "wat heb ik gisteren gedaan" daar
  // nooit. De route geeft zonder `project_id` alle banken van deze gebruiker
  // terug, en het filter op `default_off` houdt er precies de schakelbare over.
  //
  // ZONDER PROJECT ZIJN HET SINDS 22 AUGUSTUS 2026 PROJECTEN EN GEEN BANKEN
  // (R47 stap 2, Franks besluit). Achttien schakelaars in plaats van
  // negenenveertig: een lijst van 49 is niet te overzien, en hij denkt in
  // projecten. Hij wil "DOQENT erbij" en geen vier bankregels.
  //
  // DE TWEE GEVALLEN KRIJGEN DEZELFDE VORM, en dat is met opzet:
  // `{id, name, base_ids, label}`. Zo blijft er EEN component en EEN
  // schrijfweg. Wat er in `include_bases` gaat zijn en blijven BANK-ids; het
  // project is een groepering op het scherm en niet in de gegevens.
  //
  // OOK ZONDER GESPREKS-ID, want een vers gesprek heeft er nog geen en juist
  // daar wil je DOQENT vooraf kunnen aanzetten. De route laat het gesprek
  // daarom weg als het er niet is, en dan staat er eenvoudigweg niets aan.
  useEffect(() => {
    const projectVanNu = currentConversation?.project_id || selectedProjectId || null;
    let afgebroken = false;
    (async () => {
      try {
        if (!projectVanNu) {
          const resp = await axios.get(`${API}/knowledge/projects/switchable`
            + (conversationId ? `?conversation_id=${conversationId}` : ""));
          if (afgebroken) return;
          const projecten = resp.data || [];
          setArchiveBases(projecten.map(p => ({
            id: p.id,
            name: p.name,
            base_ids: p.base_ids || [],
            label: labelVoorProject(p),
            standaard_aan: Boolean(p.standaard_aan),
          })));
          // WAT ER AAN STAAT KOMT UIT DEZELFDE VRAAG, en dat is geen gemak.
          // `bases/in-conversation` levert alleen `default_off`-banken, dus een
          // aangezette projectbank zou daar niet in staan en bij een herlading
          // stil uitvallen. `on_base_ids` draagt ook de HALVE stand, die
          // ontstaat zodra er een bank bij komt na het aanzetten.
          setArchiveOn(projecten.flatMap(p => p.on_base_ids || []));
          return;
        }
        const resp = await axios.get(`${API}/knowledge/bases?project_id=${projectVanNu}`);
        // GL211-2: ook de eigen banken van het project, VAST AAN. Die weegt de orchestrator
        // altijd mee (`_project_hits`: elke bank zonder `default_off`); het menu verborg dat,
        // en zonder `default_off`-banken was er in een projectgesprek helemaal geen knop.
        if (!afgebroken) setArchiveBases((resp.data || [])
          .map(kb => ({
            id: kb.id,
            name: kb.name,
            base_ids: [kb.id],
            label: kb.default_off ? labelVoorBank(kb) : "",
            vast: !kb.default_off,
          })));
      } catch {
        // Een mislukte ophaalactie mag het gesprek niet in de weg zitten. Geen
        // schakelaar is hetzelfde als geen schakelaar aan: de bank weegt dan
        // niet mee, en dat is de veilige kant op.
        if (!afgebroken) setArchiveBases([]);
      }
    })();
    return () => { afgebroken = true; };
  }, [currentConversation?.project_id, selectedProjectId, conversationId]);

  // Wat er in DIT gesprek aan staat, na een herlading. Alleen lezen; geschreven
  // wordt er op het bericht zelf.
  //
  // ALLEEN NOG BINNEN EEN PROJECT. Zonder project komt de stand mee met de
  // projectenlijst hierboven, uit dezelfde vraag; twee vragen naar dezelfde
  // waarheid zouden het oneens kunnen worden over wat er aan staat.
  useEffect(() => {
    const projectVanNu = currentConversation?.project_id || selectedProjectId || null;
    if (!projectVanNu) return;          // zonder project doet het effect hierboven dit
    if (!conversationId) { setArchiveOn([]); return; }
    let afgebroken = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/knowledge/bases/in-conversation/${conversationId}`);
        if (!afgebroken) setArchiveOn((resp.data || []).filter(kb => kb.on_here).map(kb => kb.id));
      } catch {
        if (!afgebroken) setArchiveOn([]);
      }
    })();
    return () => { afgebroken = true; };
  }, [conversationId, currentConversation?.project_id, selectedProjectId]);

  // AANZETTEN GAAT PER REGEL EN DUS OVER AL ZIJN BANKEN. Bij een projectregel
  // zijn dat er meer; bij een bankregel precies een. Alles aan of alles uit,
  // want "DOQENT half erbij" is geen bedoeling die iemand heeft.
  //
  // GL211-2: zet je in een gesprek zonder project als eerste een project aan, dan gaat het
  // gesprek ook in dat project (`koppelProject`). Kennisbank en project zijn dan een keuze.
  const handleToggleArchive = (baseIds) => {
    const sleutel = [].concat(baseIds).join();
    const regel = archiveBases.find(r => (r.base_ids?.length ? r.base_ids : [r.id]).join() === sleutel);
    const naar = koppelProject(archiveOn, regel, currentConversation?.project_id || selectedProjectId || null);
    setArchiveOn(prev => zetOm(prev, baseIds));
    if (naar && conversationId) handleMoveToProject(conversationId, naar);
    else if (naar) setSelectedProjectId(naar);
  };

  // ALLES UIT IS EEN EIGEN HANDELING EN GEEN REEKS OMZETTINGEN. Met achttien
  // regels in het menu is terugklikken anders het enige wat er is, en achttien
  // keer `handleToggleArchive` aanroepen zou op een lijst rekenen die tussen de
  // aanroepen door niet is bijgewerkt. Hier is het een lege lijst en klaar.
  const handleAllArchiveOff = useCallback(() => setArchiveOn([]), []);
  // ZOEK OVERAL (punt i, Frank 21 okt): alle banken uit het menu aan voor dit
  // gesprek. Dezelfde schrijfweg als een klik: `include_bases` bij het volgende bericht.
  const handleAllArchiveOn = useCallback(() => setArchiveOn(alleBanken(archiveBases.filter(r => !r.vast))), [archiveBases]);

  // HET WOORD OP DE KNOP IS NIET AF TE LEIDEN UIT DE REGELS ZELF, en daarom
  // wordt het hier bepaald en meegegeven. Een project met precies een bank
  // (`Nice Tea`, `PRACTIQ`) ziet er in de gegevens identiek uit als een
  // bankregel; wie het uit `base_ids.length` zou afleiden, zou elk klein
  // project een bank noemen. Hier is het wel bekend: zonder gekozen project
  // zijn de regels PROJECTEN, daarbinnen zijn het de BANKEN van dat project.
  //
  // DEZELFDE BRON ALS DE OPHAALACTIE HIERBOVEN, en dat is geen herhaling maar
  // de eis: zou de knop op een ander begrip van "welk project" rekenen dan de
  // lijst die hij toont, dan noemt hij banken projecten precies op het moment
  // dat je een project opent.
  const archiveSoort = soortVanRegels(
    currentConversation?.project_id || selectedProjectId || null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); }
      if ((e.metaKey || e.ctrlKey) && e.key === "n") { e.preventDefault(); handleNewChat(); }
      if ((e.metaKey || e.ctrlKey) && e.key === ",") { e.preventDefault(); setSettingsOpen(true); }
      if ((e.metaKey || e.ctrlKey) && e.key === "b") { e.preventDefault(); setIsSidebarOpen(prev => !prev); }
      if (e.key === "Escape") { setSearchOpen(false); setSettingsOpen(false); }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNewChat]);

  const checkApiKeyStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/status`);
      setHasApiKey(response.data.has_api_key);
      const provider = response.data.provider || "anthropic";
      const model = response.data.model || "";
      setCurrentProvider(provider);
      setCurrentModelId(model);
      setCurrentModel(findModelLabel(provider, model));
      setWebAccess(Boolean(response.data.web_access));
      setVaultWrite(Boolean(response.data.vault_write));
    } catch (error) {
      console.error("Error checking API key status:", error);
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
    } catch (error) {
      toast.error(t("Gesprekken laden mislukt"));
    }
  }, [t]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  }, []);

  const fetchArtifacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/artifacts`);
      setArtifacts(response.data);
    } catch (error) {
      console.error("Error fetching artifacts:", error);
    }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const response = await axios.get(`${API}/conversations/${convId}/messages`);
      // The flight recorder is the single source for "where did this answer
      // come from", so a reloaded conversation shows the same marking a live
      // one does instead of a second, parallel mechanism.
      let trail = {};
      try {
        const trailResp = await axios.get(`${API}/conversations/${convId}/trail`);
        for (const entry of trailResp.data.entries || []) {
          trail[entry.message_id] = entry;
        }
      } catch {
        trail = {};
      }
      setTrailByMessage(trail);
      // FIX 1: tijdens een lopende beurt in DIT gesprek niet overschrijven. De
      // server kent de vraag en het antwoord nog niet, en overschrijven haalde
      // de antwoordbel weg: dan bleef het scherm leeg tot het einde.
      if (lopendeBeurt.current === convId) return;
      setMessages(prev => behoudDenktijd(
        response.data.map(m => (trail[m.id] ? { ...m, trail: trail[m.id] } : m)), prev));
    } catch (error) {
      if (error?.response?.status === 404) { andereWerkruimte.current(); return; }
      console.error("Error fetching messages:", error);
    }
  }, []);

  const checkCouncilStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/council/members`);
      setCouncilStatus(response.data);
    } catch (error) {
      // Leave it unknown. A council that cannot be asked about is not the
      // same as a council that is unavailable, and disabling the button on a
      // failed request would hide a working feature.
      console.error("Error checking council availability:", error);
    }
  }, []);

  // K3 (18 okt): de werkruimte. Na een wissel hoort de lijst bij de nieuwe
  // werkruimte, en het open gesprek hoort bij de oude: dat gaat dicht.
  const [teams, setTeams] = useState([]);
  const [actiefTeam, setActiefTeam] = useState(PERSOONLIJK);
  useEffect(() => {
    axios.get(`${API}/teams`).then((r) => setTeams(r.data || [])).catch(() => {});
    leesActiefTeam().then(setActiefTeam).catch(() => {});
  }, []);
  useEffect(() => {
    const gewisseld = (e) => {
      setActiefTeam(e.detail ?? PERSOONLIJK);
      fetchConversations();
      // K4 (18 okt): de projecten horen ook bij de werkruimte, en een gekozen
      // project van de oude werkruimte bestaat in de nieuwe niet.
      fetchProjects();
      setSelectedProjectId(null);
      if (conversationId) navigate("/");
    };
    window.addEventListener(WISSEL_BERICHT, gewisseld);
    return () => window.removeEventListener(WISSEL_BERICHT, gewisseld);
  }, [fetchConversations, fetchProjects, conversationId, navigate]);

  // K7a (18 okt): een wissel op een ANDER apparaat. Bij terugkeer naar dit venster
  // het actieve team opnieuw lezen; een verschil loopt via hetzelfde wisselbericht.
  useEffect(() => {
    const kijk = () => { if (document.visibilityState !== "hidden") volgWissel(actiefTeam).catch(() => {}); };
    window.addEventListener("focus", kijk);
    document.addEventListener("visibilitychange", kijk);
    return () => { window.removeEventListener("focus", kijk); document.removeEventListener("visibilitychange", kijk); };
  }, [actiefTeam]);

  // K7a: een 404 op een open gesprek betekent meestal dat een ander apparaat van
  // werkruimte wisselde. Dan geen rode fout maar een zin, de wissel volgen en terug
  // naar het startscherm. Een ref, want `fetchMessages` heeft geen afhankelijkheden.
  const andereWerkruimte = useRef(() => {});
  andereWerkruimte.current = () => {
    toast.info(t("Dit gesprek hoort bij een andere werkruimte"));
    volgWissel(actiefTeam).catch(() => {});
    navigate("/");
  };

  const handleMoveToTeam = async (convId, teamId) => {
    try {
      await verplaatsGesprek(convId, teamId);
      // Het gesprek staat nu in een andere werkruimte, dus niet meer in deze lijst.
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (conversationId === convId) navigate("/");
      toast.success(t("Gesprek verplaatst"));
    } catch {
      toast.error(t("Gesprek verplaatsen mislukt"));
    }
  };

  useEffect(() => {
    checkApiKeyStatus();
    fetchConversations();
    fetchProjects();
    fetchArtifacts();
    // Deliberately not awaited alongside the others: this one probes Ollama
    // and can sit on a timeout, and nothing on screen should wait for it.
    checkCouncilStatus();
  }, [checkApiKeyStatus, fetchConversations, fetchProjects, fetchArtifacts, checkCouncilStatus]);

  // Hoeveel er in het voorstel-bakje wachten, voor de telling in de zijbalk.
  // BLOK 7 fase A.
  //
  // **EEN KEER BIJ HET OPENEN EN NIET IN EEN LUS.** Er is in fase A geen oogst
  // die tussendoor iets toevoegt, dus pollen zou verkeer zijn zonder nieuws.
  // Fase B brengt de oogst, en dan is dit de plek om opnieuw te kijken.
  //
  // Stil bij een fout: een backend zonder deze route hoort geen rode balk op
  // te leveren voor iets wat er nog niet is. Eis 5 zegt stil of afwezig.
  // **DE TELLING HOUDT ZICHZELF BIJ, en dat was tot 24 augustus 2026 niet zo.**
  // Hij werd EEN KEER opgehaald, hier, met een lege afhankelijkheidslijst. Legde
  // de klok er daarna iets bij, dan bleef het oude getal staan tot de app opnieuw
  // werd geopend. Zie `lib/wachtendeVoorstellen.js` voor wanneer er nu opnieuw
  // wordt gekeken en waarom precies dan.
  useEffect(() => houdBij({
    haal: async () => {
      const r = await axios.get(`${API}/memory/candidates`);
      return Array.isArray(r.data) ? r.data.length : 0;
    },
    zet: setSuggestedCount,
  }), []);
  // HERMES F: de teller van de postbus, met hetzelfde ritme als Memory.
  // F1c: per lid. Wisselt het lid, dan valt het oude getal weg en telt hij opnieuw.
  const lidId = user?.id ?? null;
  useEffect(() => {
    setPostbusCount(0);
    return houdBij({
      haal: async () => {
        const r = await axios.get(`${API}/postbus/teller`);
        return Number(r.data?.aantal) || 0;
      },
      zet: setPostbusCount,
    });
  }, [lidId]);

  // FIX 1A: twee effecten, en dat is de reparatie. Het wissen hoort alleen bij
  // een ANDER gesprek. Het liep mee op `conversations`, en React Router 7
  // navigeert in een `startTransition`: het nieuwe gesprek in de lijst komt
  // dan EERST, met de oude (lege) `conversationId`, en dat wiste de lopende
  // beurt bij elke nieuwe chat. Zie `ChatPage.nieuwegesprek.test.js`.
  useEffect(() => {
    setCurrentConversation(conversationId
      ? conversations.find(c => c.id === conversationId) || null
      : null);
  }, [conversationId, conversations]);

  useEffect(() => {
    if (conversationId) {
      fetchMessages(conversationId);
    } else {
      setMessages([]);
    }
    // Consent to send prompts outward is per conversation, so switching
    // conversations means asking again.
    setEscalationDecided(false);
    setEscalationPlan(null);
    setPendingSend(null);
  }, [conversationId, fetchMessages]);

  // A proposal belongs to the exchange it came from, so it goes when you leave
  // for another conversation.
  //
  // On `conversationId` alone, deliberately. The effect above also depends on
  // `conversations`, and that list is refetched the moment an answer finishes:
  // clearing in there wiped every proposal a fraction of a second after it
  // arrived, which looked exactly like the server never sending one. Measured
  // on 29 July, twice, once with the state on the message and once with it
  // here.
  useEffect(() => {
    setProposal(null);
    setNotice(null);
  }, [conversationId]);

  const handleDeleteConversation = async (convId) => {
    try {
      await axios.delete(`${API}/conversations/${convId}`);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (currentConversation?.id === convId) navigate("/");
      toast.success(t("Gesprek verwijderd"));
    } catch (error) {
      toast.error(t("Gesprek verwijderen mislukt"));
    }
  };

  const handleRenameConversation = async (convId, newTitle) => {
    try {
      await axios.put(`${API}/conversations/${convId}`, { title: newTitle });
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, title: newTitle } : c));
      toast.success(t("Gesprek hernoemd"));
    } catch (error) {
      toast.error(t("Gesprek hernoemen mislukt"));
    }
  };

  const handleExportConversation = async (convId, format = "markdown") => {
    if (format === DOSSIER) {
      // GL215 S2: het dossier is een zip met manifest, geen JSON met inhoud.
      try {
        const response = await axios.get(`${API}/conversations/${convId}/dossier`, { responseType: "blob" });
        const url = URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = naamUitKop(response.headers?.["content-disposition"]);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(t("Dossier gedownload"));
      } catch (error) {
        toast.error(t("Dossier maken mislukt"));
      }
      return;
    }
    try {
      const response = await axios.get(`${API}/conversations/${convId}/export?format=${format}`);
      const { filename } = response.data;
      // CC5: Word en PDF komen als base64 met hun eigen mime-type.
      const blob = exportBlob(response.data, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("Geëxporteerd als {f}", { f: format === "docx" ? "Word" : format.toUpperCase() }));
    } catch (error) {
      toast.error(t("Gesprek exporteren mislukt"));
    }
  };

  const handleCreateProject = async (name) => {
    try {
      const response = await axios.post(`${API}/projects`, { name });
      setProjects(prev => [response.data, ...prev]);
      toast.success(t("Project aangemaakt"));
    } catch (error) {
      toast.error(t("Project aanmaken mislukt"));
    }
  };

  const handleNavigateToTeams = () => {
    navigate("/teams");
  };
  const handleNavigateToKnowledge = () => {
    navigate("/knowledge");
  };

  const showRoutingBlocked = (err) => {
    // A policy block is the user's own rule saying no, not an upstream
    // failure. It gets its own wording and never becomes a chat message.
    const detail = err.response?.data?.detail;
    const message = typeof detail === "object" ? detail.message : detail;
    toast.error(message || t("Tegengehouden door je routeringsregel"), {
      description: t("Pas de regel aan in Instellingen als dit niet was wat je wilde."),
      duration: 8000,
    });
    setMessages(prev => prev.filter(m => !String(m.id).startsWith("temp-")));
    setIsStreaming(false);
  };

  const resolveEscalation = (allow, dontAskAgain) => {
    const queued = pendingSend;
    setEscalationPlan(null);
    setPendingSend(null);
    if (dontAskAgain) setEscalationDecided(true);
    if (queued) handleSendMessage(queued.content, queued.attachments, allow);
  };

  const handleNavigateToProject = (projectId) => {
    navigate(`/project/${projectId}`);
  };

  const handleNavigateToArtifact = (artifactId, conversationId) => {
    const projectId = conversations.find(c => c.id === conversationId)?.project_id;
    if (projectId) {
      navigate(`/project/${projectId}/artifact/${artifactId}`);
    } else {
      navigate(`/project/unknown/artifact/${artifactId}`);
    }
  };

  const handleMoveToProject = async (convId, projectId) => {
    try {
      await axios.put(`${API}/conversations/${convId}`, { project_id: projectId || "" });
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, project_id: projectId } : c));
      if (currentConversation?.id === convId) {
        setCurrentConversation(prev => (prev ? { ...prev, project_id: projectId } : prev));
      }
      // Show where it landed. The sidebar lists either the loose chats or one
      // project's chats, so without this the conversation simply vanishes from
      // the list you were looking at, which reads as "it did not work".
      setSelectedProjectId(projectId || null);
      const name = projects.find(p => p.id === projectId)?.name;
      toast.success(projectId ? t("Verplaatst naar {naam}", { naam: name || "project" }) : t("Uit het project gehaald"));
    } catch (error) {
      toast.error(t("Gesprek verplaatsen mislukt"));
    }
  };

  const handleModelChange = async (model) => {
    try {
      await axios.put(`${API}/settings`, { model });
      setCurrentModelId(model);
      setCurrentModel(findModelLabel(currentProvider, model));
      toast.success(t("Model bijgewerkt"));
    } catch (error) {
      toast.error(t("Model bijwerken mislukt"));
    }
  };

  const handleSendMessage = async (content, attachments = [], allowEscalation = undefined) => {
    if (!content.trim() && attachments.length === 0) return;
    if (!hasApiKey) {
      setSettingsOpen(true);
      toast.error(t("Stel eerst je API-sleutel in"));
      return;
    }

    // FIX 1: de vraag en de bezig-aanduiding staan in beeld VOOR alles wat over
    // het netwerk gaat. Zie `lib/beurt.js` voor wat Frank zag en waarom.
    const nu = Date.now();
    const gebruikerId = `temp-${nu}`;
    const assistantMessage = { id: `temp-assistant-${nu}` };
    setMessages(prev => beginBeurt(prev, { inhoud: content, bijlagen: attachments,
                                           gesprekId: conversationId || "", nu }).berichten);
    setIsStreaming(true);
    const staakBeurt = () => {
      setMessages(prev => haalBeurtWeg(prev, [gebruikerId, assistantMessage.id]));
      setIsStreaming(false);
    };

    // Ask before a prompt leaves this machine, once per conversation. The
    // preflight only reports what routing would do; it sends nothing.
    if (allowEscalation === undefined && !escalationDecided) {
      try {
        const plan = (await axios.post(`${API}/routing/preflight`, { message: content })).data;
        if (plan.would_escalate) {
          staakBeurt();
          setPendingSend({ content, attachments });
          setEscalationPlan(plan);
          return;
        }
      } catch (err) {
        if (err.response?.status === 403) {
          staakBeurt();
          showRoutingBlocked(err);
          return;
        }
        // A preflight that fails for any other reason must not block sending;
        // the message endpoint runs the same check again anyway.
      }
    }

    let convId = conversationId;
    if (!convId) {
      try {
        const response = await axios.post(`${API}/conversations${selectedProjectId ? `?project_id=${selectedProjectId}` : ''}`);
        const newConv = response.data;
        convId = newConv.id;
        // I7: wat op het welkomstscherm in het plusmenu werd omgezet, geldt
        // voor dit gesprek, nu het bestaat.
        await gesprekConnectors.pasToe(convId);
        // MK2: de gekozen modelkeuze VOOR het eerste bericht, zodat al het eerste antwoord met dat model komt.
        await gesprekModel.pasToe(convId);
        // VOOR het navigeren: het navigeren laat `fetchMessages` lopen, en die
        // hoort deze beurt dan met rust te laten.
        lopendeBeurt.current = convId;
        setConversations(prev => [newConv, ...prev]);
        navigate(`/chat/${convId}`, { replace: true });
        setCurrentConversation(newConv);
      } catch (error) {
        staakBeurt();
        toast.error(t("Het gesprek kon niet worden aangemaakt."));
        return;
      }
    }
    lopendeBeurt.current = convId;

    try {
      const token = localStorage.getItem("access_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      if (councilMode) {
        try {
          // Streamed, so each member appears the moment it answers instead of
          // the screen staying empty until the slowest one is done. The
          // blocking route is still the fallback, inside runCouncil.
          const data = await runCouncil({
            api: API,
            headers,
            body: { message: content, mode: councilMode },
            onMember: (member) => setMessages(prev => prev.map(m => m.id === assistantMessage.id
              ? { ...m, council_progress: [...(m.council_progress || []), member] }
              : m
            )),
          });
          // The verdict is the answer; who agreed, who dissented and what each
          // member said is rendered as structure by MessageBubble rather than
          // flattened into the text.
          setMessages(prev => prev.map(m => m.id === assistantMessage.id
            ? { ...m, content: data.verdict?.synthesis || "", council: data, council_progress: null }
            : m
          ));
          fetchConversations();
        } catch (error) {
          if (error.reason === "routing_policy") {
            showRoutingBlocked({ response: { status: 403, data: { detail: error.detail } } });
            return;
          }
          throw error;
        }
        return;
      }

      if (orchestrateMode) {
        const resp = await fetch(`${API}/orchestrate`, {
          method: "POST",
          headers,
          body: JSON.stringify({ message: content, conversation_id: convId }),
        });
        if (!resp.ok) {
          const err = await resp.json();
          throw new Error(err.detail || t("De orkestratie is mislukt"));
        }
        const data = await resp.json();
        let output = data.result || JSON.stringify(data, null, 2);
        if (data.steps_count > 1) {
          output += `\n\n_Orchestrated across ${data.steps_count} steps in ${(data.duration_ms / 1000).toFixed(1)}s_`;
        }
        setMessages(prev => prev.map(m => m.id === assistantMessage.id
          ? { ...m, content: output, orchestration_meta: { steps: data.steps_count, delegations: data.delegations, duration_ms: data.duration_ms } }
          : m
        ));
        fetchConversations();
        return;
      }

      const response = await fetch(`${API}/conversations/${convId}/messages`, {
        method: "POST",
        headers,
        body: JSON.stringify({ content, attachments, allow_escalation: allowEscalation ?? null,
          // De VOLLEDIGE toestand van de schakelaars, altijd meegestuurd.
          // Weglaten zou "geen uitspraak" betekenen en dan zou uitzetten
          // nooit aankomen; zie het commentaar bij MessageCreate.
          include_bases: archiveOn })
      });

      if (!response.ok) {
        if (response.status === 404) { andereWerkruimte.current(); return; }  // K7a
        const errorData = await response.json();
        if (response.status === 403 && errorData.detail?.reason === "routing_policy") {
          showRoutingBlocked({ response: { status: 403, data: errorData } });
          return;
        }
        throw new Error(
          typeof errorData.detail === "string" ? errorData.detail : t("Bericht versturen mislukt")
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      // Zie `lib/sse.js`. Het netwerk levert bytes en geen regels, dus een
      // gebeurtenis kan over twee leveringen verdeeld raken; zonder buffer
      // verdwijnt zo'n halve regel stil uit het antwoord op het scherm.
      const lezer = maakLezer();
      let fullContent = "";

      while (true) {
        const { done, value } = await reader.read();
        // `stream: true` houdt losse bytes van een half aangekomen teken vast
        // in plaats van er een vraagteken van te maken. Bij het einde van de
        // stroom nog een lege ronde, zodat de decoder zijn staart kwijt kan.
        const chunk = done ? decoder.decode() : decoder.decode(value, { stream: true });
        const lines = done
          ? [...lezer.lees(chunk), ...lezer.sluit()]
          : lezer.lees(chunk);
        for (const line of lines) {
          try {
            const data = JSON.parse(line);
            if (data.error) throw new Error(data.error);
            if (data.type === "fase") {
              setMessages(prev => pasFaseToe(prev, assistantMessage.id, data.fase));
              continue;
            }
            if (data.type === "vervang") {
              // B2: de live tekst wordt het nagekeken antwoord. Zie lib/beurt.js.
              fullContent = data.content || "";
              setMessages(prev => vervangAntwoord(prev, assistantMessage.id, fullContent, data.melding));
              continue;
            }
            if (data.type === "confirmation_required") {
              setPendingClawAction(data.data);
              setClawConfirmOpen(true);
              return;
            }
            if (data.content) {
              fullContent += data.content;
              setMessages(prev => prev.map(m => m.id === assistantMessage.id ? { ...m, content: fullContent } : m));
            }
            if (data.done && data.message_id) {
              setMessages(prev => prev.map(m => m.id === assistantMessage.id
                ? {
                    ...m,
                    id: data.message_id,
                    routing: data.routing,
                    // Het taalbesluit van dit antwoord (15 oktober 2026); de
                    // kaartbeurten geven het los mee, een modelantwoord in `routing`.
                    taal: data.taal || data.routing?.taal || m.taal,
                  }
                : m
              ));
              // Held here rather than on the message. Finishing an answer
              // refetches the conversation, and a proposal hung on the message
              // object would vanish with that refetch.
              if (data.routing?.memory_proposal) {
                setProposal({ messageId: data.message_id, ...data.routing.memory_proposal });
              }
              if (data.routing?.memory_notice) {
                setNotice({ messageId: data.message_id, ...data.routing.memory_notice });
              }
            }
          } catch (e) {
            if (e.message !== "Unexpected end of JSON input") console.error("Error parsing SSE:", e);
          }
        }
        // Pas hier, en niet boven aan de lus. De laatste levering kan nog een
        // hele gebeurtenis bevatten, en die hoort verwerkt te worden voordat
        // we vertrekken.
        if (done) break;
      }
      fetchConversations();
      // GL211-1: een document dat deze beurt maakte, staat meteen onder Artefacten in de
      // zijbalk, ook in een chat zonder project. De lijst werd alleen bij het openen geladen.
      fetchArtifacts();
    } catch (error) {
      toast.error(error.message || t("Het bericht kon niet worden verstuurd."));
      setMessages(prev => prev.filter(m => m.id !== assistantMessage.id));
    } finally {
      setIsStreaming(false);
      // De beurt is klaar; nu mag de lijst weer van de server komen, met de
      // echte ids en het spoor erbij.
      if (lopendeBeurt.current === convId) {
        lopendeBeurt.current = null;
        fetchMessages(convId);
      }
    }
  };

  /** POST G: stop het antwoord dat nu loopt. De dienst maakt er een zin van
   *  ("Gestopt: je drukte op Stop") die via de stroom gewoon binnenkomt. */
  const handleStop = async () => {
    const id = lopendeBeurt.current;
    if (!id) return;
    try {
      await axios.post(`${API}/conversations/${id}/stop`);
    } catch {
      toast.error(t("Stoppen lukte niet; het antwoord loopt nog."));
    }
  };

  /** Take a proposal off the screen without storing it.
   *
   *  Nothing to tell the server: the proposal was never stored, it only
   *  travelled with the answer. Dropping it here is the whole of "no thanks".
   */
  const clearProposal = () => setProposal(null);

  /** Nee zeggen, en dat ook LATEN WETEN. 5 september 2026.
   *
   *  **HIER STOND ALLEEN `clearProposal`, en dat is de hele bug.** De kaart
   *  verdween van het scherm en er ging niets naar de backend, dus stond de
   *  volgende keer dezelfde zin er weer. Frank kreeg hem vijftien keer.
   *
   *  De route `POST /api/memory/no-list` bestond al en droeg in zijn eigen kop
   *  "dit is de route van de kaart per bericht; zonder deze route komt dezelfde
   *  zin eeuwig terug". Niemand riep hem aan.
   *
   *  **DE KAART GAAT SOWIESO WEG.** Faalt het verzoek, dan is het antwoord nog
   *  steeds nee voor NU; hem laten staan omdat de server niet reageerde zou de
   *  weigering in een storing veranderen. Wel een melding, want stil falen is
   *  precies hoe deze bug zo lang kon blijven staan.
   */
  const handleDismissProposal = async () => {
    const weg = proposal;
    clearProposal();
    if (!weg) return;
    try {
      await axios.post(`${API}/memory/no-list`, {
        text: weg.text,
        fact_key: weg.fact_key || "",
        source: "something you said",
      });
    } catch {
      toast.error(t("Voor nu genoteerd, maar deze kan terugkomen: het opslaan van je nee lukte niet"));
    }
  };

  /** Accept it, which is the only way a statement about you gets stored. */
  const handleRememberProposal = async () => {
    if (!proposal) return;
    try {
      await axios.post(`${API}/memory`, {
        text: proposal.text,
        fact_key: proposal.fact_key || "",
        source: "something you said",
      });
      clearProposal();
      toast.success(t("Onthouden: {tekst}", { tekst: proposal.text }));
    } catch {
      toast.error(t("Kon dat niet onthouden"));
    }
  };

  const handleConfirmClawAction = async () => {
    if (!pendingClawAction) return;
    setClawConfirmOpen(false);
    const token = localStorage.getItem("access_token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    try {
      const resp = await fetch(`${API}/claw/confirm`, {
        method: "POST",
        headers,
        body: JSON.stringify({ session_id: pendingClawAction.session_id }),
      });
      if (!resp.ok) {
        toast.error(t("De handeling is mislukt"));
        setPendingClawAction(null);
        return;
      }
      const data = await resp.json();
      if (data.requires_confirmation) {
        setPendingClawAction({ session_id: data.session_id, tool: data.tool, args: data.args, description: data.description });
        setClawConfirmOpen(true);
        return;
      }
      if (data.result) {
        setMessages(prev => prev.map(m =>
          m.id.startsWith("temp-assistant") ? { ...m, content: data.result } : m
        ));
        toast.success(t("Handeling uitgevoerd"));
      }
    } catch (err) {
      toast.error(t("De handeling kon niet worden uitgevoerd"));
    }
    setPendingClawAction(null);
  };

  const handleCancelClawAction = async () => {
    if (!pendingClawAction) return;
    setClawConfirmOpen(false);
    const token = localStorage.getItem("access_token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    try {
      await fetch(`${API}/claw/cancel`, {
        method: "POST",
        headers,
        body: JSON.stringify({ session_id: pendingClawAction.session_id }),
      });
    } catch (err) {
      console.error("Cancel error:", err);
    }
    setPendingClawAction(null);
    setMessages(prev => prev.filter(m => !m.id.startsWith("temp-assistant")));
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background" data-testid="chat-page">
      <Sidebar
        conversations={conversations}
        currentConversationId={conversationId}
        isOpen={isSidebarOpen}
        isNarrow={isNarrow}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        // On a phone the sidebar covers the chat, so picking something from it
        // has to get you to that thing, not leave you looking at the menu.
        onNewChat={() => { handleNewChat(); if (isNarrow) setIsSidebarOpen(false); }}
        onSelectConversation={(id) => { navigate(`/chat/${id}`); if (isNarrow) setIsSidebarOpen(false); }}
        onDeleteConversation={handleDeleteConversation}
        onRenameConversation={handleRenameConversation}
        onExportConversation={handleExportConversation}
        onOpenSettings={(tabblad) => { if (typeof tabblad === "string") setSettingsTab(tabblad); setSettingsOpen(true); }}
        onOpenSearch={() => setSearchOpen(true)}
        onOpenTimeMachine={() => setTimeMachineOpen(true)}
        projects={projects}
        artifacts={artifacts}
        onCreateProject={handleCreateProject}
        onSelectProject={setSelectedProjectId}
        selectedProjectId={selectedProjectId}
        onMoveToProject={handleMoveToProject}
        teams={teams}
        actiefTeam={actiefTeam}
        onMoveToTeam={handleMoveToTeam}
        user={user}
        onOpenLogin={() => navigate("/login")}
        onSignOut={() => {
          // Clearing the tokens is what ends the session. The navigation is
          // only so the screen behind it does not sit there empty; without a
          // session RequireSession would send us here anyway.
          logout();
          navigate("/login");
        }}
        onOpenUpgrade={() => setUpgradeOpen(true)}
        onNavigateToProject={handleNavigateToProject}
        onNavigateToArtifact={handleNavigateToArtifact}
        onNavigateToTeams={handleNavigateToTeams}
        onNavigateToKnowledge={handleNavigateToKnowledge}
        onNavigateToAgents={() => navigate("/agents")}
        onNavigateToCode={() => navigate("/code")}
        onNavigateToMemory={() => navigate("/memory")}
        suggestedCount={suggestedCount}
        onNavigateToPostbus={() => navigate("/postbus")}
        postbusCount={postbusCount}
      />
      
      <div className="flex min-w-0 flex-1 flex-col">
      {/* F3c keuze 5: tijdens het respijt ziet iedereen dat de licentie verlopen is. */}
      <LicentieMelding />
      <ChatArea
        // GEEN POP-UP: een regel die niets onderbreekt en op een klik weg is
        // (zie components/WachtInGeheugen.jsx). PUNT 4b: hij staat ONDER de kop
        // van ChatArea, binnen de veilige zone, en niet meer over de klok.
        bovenbalk={
          <WachtInGeheugen
            aantal={suggestedCount}
            onGaErheen={() => navigate("/memory")}
          />
        }
        messages={messages}
        isStreaming={isStreaming}
        onSendMessage={handleSendMessage}
        onStop={handleStop}
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        isSidebarOpen={isSidebarOpen}
        hasApiKey={hasApiKey}
        webAccess={webAccess}
        vaultWrite={vaultWrite}
        onOpenSettings={(tabblad) => { if (typeof tabblad === "string") setSettingsTab(tabblad); setSettingsOpen(true); }}
        onOpenSearch={() => setSearchOpen(true)}
        onOpenTimeMachine={() => setTimeMachineOpen(true)}
        onNewChat={handleNewChat}
        currentModel={currentModel}
        currentProvider={currentProvider}
        onModelChange={handleModelChange}
        modelKeuze={gesprekModel.keuze}
        standaardModel={{ provider: currentProvider, model: currentModelId }}
        onKiesModel={gesprekModel.kies}
        onKiesNiveau={gesprekModel.kiesNiveau}
        plusOpties={gesprekModel.opties}
        onZetOptie={gesprekModel.zetOptie}
        orchestrateMode={orchestrateMode}
        onToggleOrchestrate={() => setOrchestrateMode(!orchestrateMode)}
        councilMode={councilMode}
        onSetCouncilMode={setCouncilMode}
        councilStatus={councilStatus}
        projects={projects}
        projectId={currentConversation?.project_id || null}
        archiveBases={archiveBases}
        archiveOn={archiveOn}
        onToggleArchive={handleToggleArchive}
        onAllArchiveOff={handleAllArchiveOff}
        onAllArchiveOn={handleAllArchiveOn}
        archiveSoort={archiveSoort}
        connectors={gesprekConnectors.connectors}
        connectorRol={gesprekConnectors.rol}
        onToggleConnector={(id, aan, soort) => gesprekConnectors.zet(id, aan, soort).catch(e =>
          toast.error(e.response?.data?.detail || t("Dat lukte niet. Er is niets gewijzigd.")))}
        onOpenSkills={() => navigate("/skills")}
        gesprekId={conversationId || null}
        onKoppelingBeslist={() => conversationId && fetchMessages(conversationId)}
        onOpenProject={(id) => navigate(`/project/${id}`)}
        onMoveToProject={(id) => currentConversation && handleMoveToProject(currentConversation.id, id)}
        proposal={proposal}
        onRememberProposal={handleRememberProposal}
        onDismissProposal={handleDismissProposal}
        notice={notice}
      />
      </div>

      {/* H3 (Frank, 18 okt 2026): vraagt om een basismap als die er nog niet is. */}
      <EersteStart />
      <SettingsDialog open={settingsOpen} tab={settingsTab}
        onOpenChange={(open) => { setSettingsOpen(open); if (!open) setSettingsTab("provider"); }} onSave={() => { checkApiKeyStatus(); checkCouncilStatus(); toast.success(t("Instellingen opgeslagen")); }} />
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} onSelectConversation={(id) => { navigate(`/chat/${id}`); setSearchOpen(false); }} />
      <TimeMachine open={timeMachineOpen} onOpenChange={setTimeMachineOpen} onSelectConversation={(id) => { navigate(`/chat/${id}`); setTimeMachineOpen(false); }} />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} user={user} />
      <EscalationConfirmDialog
        open={Boolean(escalationPlan)}
        plan={escalationPlan}
        onSendExternally={(dontAskAgain) => resolveEscalation(true, dontAskAgain)}
        onKeepLocal={(dontAskAgain) => resolveEscalation(false, dontAskAgain)}
      />
      <ClawConfirmDialog
        open={clawConfirmOpen}
        onOpenChange={(open) => { if (!open) handleCancelClawAction(); setClawConfirmOpen(open); }}
        pendingAction={pendingClawAction}
        onConfirm={handleConfirmClawAction}
        onCancel={handleCancelClawAction}
      />
    </div>
  );
}
